/**
 * SmartValue - Renders values with smart type detection
 * Detects URLs, emails, phones and renders them as clickable links
 * Includes truncation, tooltips, and copy-to-clipboard
 */
import { useState } from 'react';
import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { detectValueType, getValueHref, truncateText, isEmptyValue } from '@diggens/shared-utils';
import type { ValueType } from '@diggens/shared-utils';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

/**
 * Copy text to clipboard
 * Returns true if successful
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}

interface SmartValueProps {
  value: string;
  name: string;
  maxLength?: number;
  showCopy?: boolean;
  variant?: 'body2' | 'caption';
  fontWeight?: number;
}

export function SmartValue({ value, name, maxLength = 50, showCopy = true, variant = 'body2', fontWeight = 600 }: SmartValueProps) {
  const [copied, setCopied] = useState(false);

  if (isEmptyValue(value)) {
    return (
      <Typography variant={variant} color="text.disabled" fontStyle="italic">
        —
      </Typography>
    );
  }

  const valueType: ValueType = detectValueType(name, value);
  const href = getValueHref(value, valueType);
  const displayValue = truncateText(value, maxLength);
  const isTruncated = value.length > maxLength;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const success = await copyToClipboard(value);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const renderValue = () => {
    if (href) {
      return (
        <Link
          href={href}
          target={valueType === 'url' ? '_blank' : undefined}
          rel={valueType === 'url' ? 'noopener noreferrer' : undefined}
          onClick={(e) => e.stopPropagation()}
          sx={{
            color: 'primary.main',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': { textDecoration: 'underline' },
            wordBreak: 'break-word',
          }}
        >
          {displayValue}
          {valueType === 'url' && <ExternalLink size={12} strokeWidth={ICON_STROKE} />}
        </Link>
      );
    }

    return (
      <Typography
        variant={variant}
        fontWeight={fontWeight}
        sx={{
          wordBreak: 'break-word',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {displayValue}
      </Typography>
    );
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <Tooltip title={isTruncated ? value : ''} placement="top" arrow>
        <Box sx={{ flex: 1, minWidth: 0 }}>{renderValue()}</Box>
      </Tooltip>
      {showCopy && (
        <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
          <IconButton
            className="copy-icon"
            size="small"
            onClick={handleCopy}
            sx={{
              p: 0.25,
              opacity: 0,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 1 },
            }}
          >
            {copied ? <Check size={ICON_SIZES.xs} strokeWidth={ICON_STROKE} color="green" /> : <Copy size={ICON_SIZES.xs} strokeWidth={ICON_STROKE} />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
